import { get, create, updatePatch, deleteResource } from "./apiService";
import { getStackableAssets } from "./assetService";
import { getMarketAssets } from "./marketService";
import type { LegacyAsset } from "../types/api";
import {
  resolveBusinessRoute,
  resolveBusinessUrlWithQuery,
  withBusinessContext,
} from "../utils/businessRoute";

export interface AutoStackSchedule extends Record<string, unknown> {
  time: string;
  dayOfWeek?: number | string;
  dayOfMonth?: number;
}

export interface AutoStack {
  _id: string;
  asset: string | LegacyAsset;
  assetSymbol: string;
  name?: string;
  amount: number;
  frequency: "daily" | "weekly" | "biweekly" | "monthly";
  schedule: AutoStackSchedule;
  fundingType: string;
  funding_type?: string;
  startDate: string;
  start_date?: string;
  status: "active" | "paused" | "stopped" | "inactive" | "cancelled";
  asset_balance?: number;
  fiat_balance?: number;
  next_purchase?: AutoStackPurchase;
  nextPurchaseId?: string;
  purchases?: AutoStackPurchase[];
  [key: string]: unknown;
}

export interface CreateAutoStackPayload {
  asset: string;
  assetSymbol: string;
  name?: string;
  amount: number;
  frequency: "daily" | "weekly" | "biweekly" | "monthly";
  schedule: AutoStackSchedule;
  fundingType: string;
  startDate: string;
}

export interface UpdateAutoStackPayload {
  name?: string;
  amount?: number;
  frequency?: "daily" | "weekly" | "biweekly" | "monthly";
  schedule?: Partial<AutoStackSchedule>;
  fundingType?: string;
  status?: "active" | "paused";
}

export interface AutoStackListResponse {
  autoStacks: AutoStack[];
  total: number;
  page: number;
  limit: number;
  [key: string]: unknown;
}

export interface AutoStackPurchase {
  _id: string;
  user_id: string;
  user_autostack: string;
  scheduled_purchase_date: string;
  start_date: string;
  amount_fiat: number;
  amount_asset: number;
  status: "scheduled" | "pending" | "completed" | "failed" | "cancelled";
  type: string;
  attempts: number;
  max_attempts: number;
  createdAt: string;
  updatedAt: string;
  funding_type?: string;
  [key: string]: unknown;
}

export interface AutoStackPurchasesResponse {
  results: AutoStackPurchase[];
  totalPages: number;
  totalResults: number;
  page: number;
  limit: number;
  [key: string]: unknown;
}

interface RawAutoStackPurchase {
  _id: string;
  userId?: string;
  user_id?: string;
  userAutoStack?: string;
  user_autostack?: string;
  scheduledPurchaseDate?: string;
  scheduled_purchase_date?: string;
  startDate?: string;
  start_date?: string;
  amountFiat?: number | string | DecimalLike;
  amount_fiat?: number | string | DecimalLike;
  amountAsset?: number | string;
  amount_asset?: number | string | DecimalLike;
  status?: string;
  type?: string;
  attempts?: number;
  max_attempts?: number;
  maxAttempts?: number;
  createdAt?: string;
  updatedAt?: string;
  fundingType?: string;
  funding_type?: string;
}

interface RawAutoStackResponse {
  _id: string;
  asset:
    | string
    | LegacyAsset
    | { _id?: string; symbol?: string; name?: string; image?: string };
  assetSymbol?: string;
  name?: string;
  amount: number | string | DecimalLike;
  frequency: "daily" | "weekly" | "biweekly" | "monthly";
  schedule: AutoStackSchedule;
  fundingType?: string;
  funding_type?: string;
  startDate?: string;
  start_date?: string;
  status?: "active" | "paused" | "stopped" | "inactive" | "cancelled";
  assetBalance?: number | string;
  asset_balance?: number | string;
  fiatBalance?: number | string;
  fiat_balance?: number | string;
  nextPurchase?: RawAutoStackPurchase | string;
  next_purchase?: RawAutoStackPurchase | string;
  purchases?: RawAutoStackPurchase[];
  createdAt?: string;
  updatedAt?: string;
  reference?: string;
  bank_transfer_reference?: string;
}

interface RawAutoStackListResponse {
  autoStacks?: RawAutoStackResponse[];
  results?: RawAutoStackResponse[];
  total?: number;
  page?: number;
  limit?: number;
}

interface RawAutoStackPurchasesResponse {
  purchases?: RawAutoStackPurchase[];
  total?: number;
  page?: number;
  limit?: number;
}

export interface AutoStackStatsResponse {
  assetBalance: number;
  totalStacked: number;
  totalAccumulated: number;
  avgBuyPrice: number;
  totalBalance: number;
  scheduledFiatTotal: number;
  performance: Array<{
    accumulatedAsset: number;
    fiatValue: number;
    timestamp: string;
  }>;
  labels: string[];
}

interface AutoStackTemplateResponse {
  name: string;
  asset: LegacyAsset;
  amount: number;
  frequency: "daily" | "weekly" | "monthly";
  schedule: AutoStackSchedule;
  estimated12MonthAccumulation: Record<string, number>;
}

interface AddToAutoStackPayload {
  amount?: number;
  stackType?: string;
}

interface DecimalLike {
  $numberDecimal?: string;
  toString?: () => string;
}

interface NormalizedStackAsset extends LegacyAsset {
  image?: string;
}

interface MarketPriceEntry {
  priceInZar?: number;
  price?: number;
}

function toNumber(value: unknown): number {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  if (value && typeof value === "object") {
    const decimalValue = (value as DecimalLike).$numberDecimal;
    if (typeof decimalValue === "string") {
      const parsed = parseFloat(decimalValue);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    if (typeof (value as DecimalLike).toString === "function") {
      const stringValue = (value as DecimalLike).toString?.();
      const parsed = stringValue ? parseFloat(stringValue) : NaN;
      return Number.isFinite(parsed) ? parsed : 0;
    }
  }

  return 0;
}

function normalizeDayOfWeekToFormValue(dayOfWeek: unknown): string | undefined {
  if (typeof dayOfWeek === "string") {
    const lower = dayOfWeek.trim().toLowerCase();
    if (
      [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ].includes(lower)
    ) {
      return lower;
    }
    if (/^\d+$/.test(lower)) {
      return normalizeDayOfWeekToFormValue(parseInt(lower, 10));
    }
    return undefined;
  }
  if (typeof dayOfWeek !== "number" || !Number.isFinite(dayOfWeek)) {
    return undefined;
  }
  const map: Record<number, string> = {
    0: "sunday",
    1: "monday",
    2: "tuesday",
    3: "wednesday",
    4: "thursday",
    5: "friday",
    6: "saturday",
    7: "sunday",
  };
  return map[dayOfWeek];
}

function normalizeScheduleFromApi(
  schedule: AutoStackSchedule | undefined
): AutoStackSchedule {
  if (!schedule || typeof schedule !== "object") {
    return { time: "09:00" };
  }
  const out: AutoStackSchedule = {
    ...schedule,
    time: typeof schedule.time === "string" ? schedule.time : "09:00",
  };
  if ("dayOfWeek" in schedule && schedule.dayOfWeek !== undefined) {
    const mapped = normalizeDayOfWeekToFormValue(schedule.dayOfWeek);
    if (mapped !== undefined) {
      out.dayOfWeek = mapped;
    }
  }
  return out;
}

function normalizeStackAsset(
  asset: RawAutoStackResponse["asset"]
): string | NormalizedStackAsset {
  if (typeof asset === "string") {
    return asset;
  }

  if (!asset || typeof asset !== "object") {
    return "";
  }

  const legacyAsset = asset as LegacyAsset & {
    symbol?: string;
    name?: string;
    image?: string;
  };

  const symbol = legacyAsset.asset || legacyAsset.symbol || "";
  const assetName = legacyAsset.assetName || legacyAsset.name || symbol;
  const image = legacyAsset.image || legacyAsset.assetIcon;

  return {
    ...legacyAsset,
    asset: symbol,
    assetName,
    assetIcon: legacyAsset.assetIcon || image || null,
    image: image || undefined,
  };
}

function normalizePurchaseStatus(status?: string): AutoStackPurchase["status"] {
  if (status === undefined || status === null || status === "") {
    return "pending";
  }

  const normalized = String(status).trim().toLowerCase();

  if (
    normalized === "success" ||
    normalized === "successful" ||
    normalized === "complete" ||
    normalized === "completed" ||
    normalized === "succeeded"
  ) {
    return "completed";
  }

  if (
    normalized === "scheduled" ||
    normalized === "pending" ||
    normalized === "failed" ||
    normalized === "cancelled" ||
    normalized === "canceled"
  ) {
    return normalized === "canceled"
      ? "cancelled"
      : (normalized as AutoStackPurchase["status"]);
  }

  return "pending";
}

function normalizeNextPurchaseFromApi(
  nextCamel: unknown,
  nextSnake: unknown
): { next_purchase?: AutoStackPurchase; nextPurchaseId?: string } {
  const raw =
    nextCamel !== undefined && nextCamel !== null ? nextCamel : nextSnake;
  if (raw === undefined || raw === null) {
    return {};
  }
  if (typeof raw === "string") {
    const id = raw.trim();
    return id ? { nextPurchaseId: id } : {};
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return { next_purchase: normalizePurchase(raw as RawAutoStackPurchase) };
  }
  return {};
}

function normalizePurchase(purchase: RawAutoStackPurchase): AutoStackPurchase {
  return {
    _id: purchase._id,
    user_id: purchase.userId || purchase.user_id || "",
    user_autostack: purchase.userAutoStack || purchase.user_autostack || "",
    scheduled_purchase_date:
      purchase.scheduledPurchaseDate ||
      purchase.scheduled_purchase_date ||
      purchase.createdAt ||
      "",
    start_date:
      purchase.startDate || purchase.start_date || purchase.createdAt || "",
    amount_fiat: toNumber(purchase.amountFiat ?? purchase.amount_fiat),
    amount_asset: toNumber(purchase.amountAsset ?? purchase.amount_asset),
    status: normalizePurchaseStatus(purchase.status),
    type: purchase.type || "scheduled",
    attempts: purchase.attempts || 0,
    max_attempts: purchase.maxAttempts || purchase.max_attempts || 0,
    createdAt: purchase.createdAt || "",
    updatedAt: purchase.updatedAt || "",
    funding_type: purchase.fundingType || purchase.funding_type,
  };
}

function normalizeAutoStack(autoStack: RawAutoStackResponse): AutoStack {
  const normalizedAsset = normalizeStackAsset(autoStack.asset);
  const assetSymbol =
    autoStack.assetSymbol ||
    (typeof normalizedAsset === "string"
      ? normalizedAsset
      : normalizedAsset.asset) ||
    "";
  const { next_purchase, nextPurchaseId } = normalizeNextPurchaseFromApi(
    autoStack.nextPurchase,
    autoStack.next_purchase
  );

  return {
    ...autoStack,
    asset: normalizedAsset,
    assetSymbol,
    amount: toNumber(autoStack.amount),
    fundingType: autoStack.fundingType || autoStack.funding_type || "wallet",
    startDate: autoStack.startDate || autoStack.start_date || "",
    schedule: normalizeScheduleFromApi(autoStack.schedule),
    status:
      autoStack.status === "cancelled" || autoStack.status === "inactive"
        ? "stopped"
        : autoStack.status || "active",
    assetBalance:
      autoStack.assetBalance !== undefined
        ? toNumber(autoStack.assetBalance)
        : toNumber(autoStack.asset_balance),
    fiatBalance:
      autoStack.fiatBalance !== undefined
        ? toNumber(autoStack.fiatBalance)
        : toNumber(autoStack.fiat_balance),
    funding_type: autoStack.fundingType || autoStack.funding_type || "wallet",
    start_date: autoStack.startDate || autoStack.start_date || "",
    asset_balance:
      autoStack.assetBalance !== undefined
        ? toNumber(autoStack.assetBalance)
        : toNumber(autoStack.asset_balance),
    fiat_balance:
      autoStack.fiatBalance !== undefined
        ? toNumber(autoStack.fiatBalance)
        : toNumber(autoStack.fiat_balance),
    next_purchase,
    nextPurchaseId,
    nextPurchase: undefined,
    purchases: (autoStack.purchases || []).map(normalizePurchase),
  };
}

function normalizeAutoStackList(response: RawAutoStackListResponse): {
  results: AutoStack[];
  totalPages: number;
  totalResults: number;
  page: number;
  limit: number;
} {
  const page = Math.max(1, response.page || 1);
  const limit = Math.max(1, response.limit || 20);
  const totalResults = Math.max(0, response.total || 0);
  const totalPages = Math.max(1, Math.ceil(totalResults / limit));

  const rows = response.autoStacks ?? response.results ?? [];

  return {
    results: rows.map(normalizeAutoStack),
    totalPages,
    totalResults,
    page,
    limit,
  };
}

function normalizePurchasesResponse(
  response: RawAutoStackPurchasesResponse
): AutoStackPurchasesResponse {
  const page = Math.max(1, response.page || 1);
  const limit = Math.max(1, response.limit || 20);
  const totalResults = Math.max(0, response.total || 0);
  const totalPages = Math.max(1, Math.ceil(totalResults / limit));

  return {
    results: (response.purchases || []).map(normalizePurchase),
    totalPages,
    totalResults,
    page,
    limit,
  };
}

const autoStackPath = (suffix: string, businessId?: string | null): string =>
  resolveBusinessRoute(businessId, `/api/v2/auto-stack${suffix}`);

const autoStackUrlWithBusinessQuery = (
  suffix: string,
  businessId?: string | null
): string =>
  resolveBusinessUrlWithQuery(businessId, `/api/v2/auto-stack${suffix}`);

export const getUserAutoStacks = async (
  page = 1,
  limit = 20,
  status?: string,
  businessId?: string | null
): Promise<{
  results: AutoStack[];
  totalPages: number;
  totalResults: number;
  page: number;
  limit: number;
}> => {
  const params: Record<string, unknown> = { page, limit };
  if (status) params.status = status;
  const response = await get<RawAutoStackListResponse>(
    autoStackPath("", businessId),
    withBusinessContext(businessId, params)
  );
  return normalizeAutoStackList(response);
};

export const getUserSingleAutoStack = async (
  stackId: string,
  businessId?: string | null
): Promise<AutoStack> => {
  const response = await get<RawAutoStackResponse>(
    autoStackPath(`/${stackId}`, businessId),
    withBusinessContext(businessId)
  );
  return normalizeAutoStack(response);
};

export const createAutoStack = async (
  data: CreateAutoStackPayload,
  businessId?: string | null
): Promise<AutoStack> => {
  return create<AutoStack>(
    autoStackUrlWithBusinessQuery("", businessId),
    data as unknown as Record<string, unknown>
  );
};

export const updateUserAutoStack = async (
  stackId: string,
  updateData: UpdateAutoStackPayload,
  businessId?: string | null
): Promise<AutoStack> => {
  if (updateData.status === "paused") {
    return pauseAutoStack(stackId, businessId) as Promise<AutoStack>;
  }

  if (updateData.status === "active") {
    return resumeAutoStack(stackId, businessId) as Promise<AutoStack>;
  }

  const response = await updatePatch<RawAutoStackResponse>(
    autoStackPath(`/${stackId}`, businessId),
    withBusinessContext(businessId),
    updateData as unknown as Record<string, unknown>
  );
  return normalizeAutoStack(response);
};

export const pauseAutoStack = async (
  stackId: string,
  businessId?: string | null
): Promise<AutoStack> => {
  const response = await updatePatch<RawAutoStackResponse>(
    autoStackPath(`/${stackId}/pause`, businessId),
    withBusinessContext(businessId)
  );
  return normalizeAutoStack(response);
};

export const resumeAutoStack = async (
  stackId: string,
  businessId?: string | null
): Promise<AutoStack> => {
  const response = await updatePatch<RawAutoStackResponse>(
    autoStackPath(`/${stackId}/resume`, businessId),
    withBusinessContext(businessId)
  );
  return normalizeAutoStack(response);
};

export const deleteAutoStack = async (
  stackId: string,
  businessId?: string | null
): Promise<unknown> => {
  return deleteResource(
    autoStackPath(`/${stackId}`, businessId),
    withBusinessContext(businessId)
  );
};

export const getAutoStackPurchases = async (
  stackId: string,
  page = 1,
  limit = 20,
  businessId?: string | null
): Promise<AutoStackPurchasesResponse> => {
  const response = await get<RawAutoStackPurchasesResponse>(
    autoStackPath(`/${stackId}/purchases`, businessId),
    withBusinessContext(businessId, { page, limit })
  );
  return normalizePurchasesResponse(response);
};

const ALL_PURCHASES_PAGE_SIZE = 100;

export const getAllAutoStackPurchases = async (
  stackId: string,
  businessId?: string | null
): Promise<AutoStackPurchasesResponse> => {
  const first = await getAutoStackPurchases(
    stackId,
    1,
    ALL_PURCHASES_PAGE_SIZE,
    businessId
  );

  if (first.totalPages <= 1) {
    return first;
  }

  const remainingPages = await Promise.all(
    Array.from({ length: first.totalPages - 1 }, (_, index) =>
      getAutoStackPurchases(
        stackId,
        index + 2,
        ALL_PURCHASES_PAGE_SIZE,
        businessId
      )
    )
  );

  const results = [
    ...first.results,
    ...remainingPages.flatMap((response) => response.results),
  ];

  return {
    ...first,
    results,
    page: 1,
    limit: results.length,
    totalPages: 1,
    totalResults: first.totalResults || results.length,
  };
};

export const getAutoStackStats = async (
  businessId?: string | null
): Promise<AutoStackStatsResponse> => {
  const [stackPage, marketResponse] = await Promise.all([
    getUserAutoStacks(1, 100, undefined, businessId),
    getMarketAssets(),
  ]);
  const stacks = stackPage.results;
  const marketDataEntries = Object.entries(
    (marketResponse.data || {}) as Record<string, MarketPriceEntry>
  );
  const marketData = new Map<string, MarketPriceEntry>(marketDataEntries);

  if (stacks.length === 0) {
    return {
      assetBalance: 0,
      totalStacked: 0,
      totalAccumulated: 0,
      avgBuyPrice: 0,
      totalBalance: 0,
      scheduledFiatTotal: 0,
      performance: [],
      labels: [],
    };
  }

  const purchasesByStack = await Promise.all(
    stacks.map(async (stack) => ({
      stack,
      purchases: await getAllAutoStackPurchases(stack._id, businessId),
    }))
  );

  const successfulPurchases = purchasesByStack.flatMap(({ stack, purchases }) =>
    purchases.results
      .filter((purchase) => purchase.status === "completed")
      .map((purchase) => ({ purchase, stack }))
  );

  const scheduledPurchases = purchasesByStack.flatMap(({ purchases }) =>
    purchases.results.filter(
      (purchase) =>
        purchase.status === "scheduled" || purchase.status === "pending"
    )
  );
  const scheduledFiatTotal = scheduledPurchases.reduce(
    (sum, purchase) => sum + purchase.amount_fiat,
    0
  );

  const totalStacked = successfulPurchases.reduce(
    (sum, item) => sum + item.purchase.amount_fiat,
    0
  );
  const totalAccumulated = successfulPurchases.reduce(
    (sum, item) => sum + item.purchase.amount_asset,
    0
  );
  const totalBalance = stacks.reduce((sum, stack) => {
    const symbol = stack.assetSymbol?.toUpperCase();
    const stackAssetBalance = toNumber(stack.asset_balance);
    const purchasedAssetBalance = successfulPurchases
      .filter((item) => item.stack._id === stack._id)
      .reduce(
        (purchaseSum, item) => purchaseSum + item.purchase.amount_asset,
        0
      );
    const assetBalance =
      stackAssetBalance > 0 ? stackAssetBalance : purchasedAssetBalance;
    const explicitFiatBalance = toNumber(stack.fiat_balance);
    const marketEntry = symbol ? marketData.get(symbol) : undefined;
    const marketPrice = marketEntry?.priceInZar || marketEntry?.price || 0;

    return (
      sum +
      (explicitFiatBalance > 0
        ? explicitFiatBalance
        : assetBalance * marketPrice)
    );
  }, 0);

  let runningAsset = 0;
  let runningFiatValue = 0;
  const performance = successfulPurchases
    .sort(
      (left, right) =>
        new Date(left.purchase.scheduled_purchase_date).getTime() -
        new Date(right.purchase.scheduled_purchase_date).getTime()
    )
    .map(({ purchase, stack }) => {
      const stackSymbol = stack.assetSymbol?.toUpperCase();
      const stackAssetBalance = toNumber(stack.asset_balance);
      const stackFiatBalance = toNumber(stack.fiat_balance);
      const unitPriceFromStack =
        stackAssetBalance > 0 ? stackFiatBalance / stackAssetBalance : 0;
      const marketEntry = stackSymbol ? marketData.get(stackSymbol) : undefined;
      const unitPrice =
        unitPriceFromStack ||
        marketEntry?.priceInZar ||
        marketEntry?.price ||
        0;

      runningAsset += purchase.amount_asset;
      runningFiatValue += purchase.amount_asset * unitPrice;

      return {
        accumulatedAsset: runningAsset,
        fiatValue: runningFiatValue,
        timestamp: purchase.scheduled_purchase_date,
      };
    });

  if (performance.length === 0 && totalBalance > 0) {
    performance.push({
      accumulatedAsset: 0,
      fiatValue: totalBalance,
      timestamp: new Date().toISOString(),
    });
  }

  return {
    assetBalance: totalAccumulated,
    totalStacked,
    totalAccumulated,
    avgBuyPrice: totalAccumulated > 0 ? totalStacked / totalAccumulated : 0,
    totalBalance,
    scheduledFiatTotal,
    performance,
    labels: performance.map((point) => point.timestamp),
  };
};

export const getAutoStackTemplates = async (): Promise<{
  results: AutoStackTemplateResponse[];
}> => {
  const [stackableAssets, marketResponse] = await Promise.all([
    getStackableAssets(),
    getMarketAssets(),
  ]);

  const templates = (stackableAssets.data || []).slice(0, 6).map((asset) => {
    const baseAmount = Math.max(500, asset.minStackAmount || 500);
    const marketEntry = (marketResponse.data || {})[
      asset.asset.toUpperCase()
    ] as { priceInZar?: number; price?: number } | undefined;
    const priceInZar =
      typeof marketEntry?.priceInZar === "number" && marketEntry.priceInZar > 0
        ? marketEntry.priceInZar
        : typeof marketEntry?.price === "number" && marketEntry.price > 0
          ? marketEntry.price
          : 0;
    const estimatedAsset = priceInZar > 0 ? (baseAmount * 12) / priceInZar : 0;

    return {
      name: `My ${asset.assetName} Vault`,
      asset,
      amount: baseAmount,
      frequency: "monthly" as const,
      schedule: { time: "09:00" },
      estimated12MonthAccumulation: {
        [asset.asset]: estimatedAsset,
        zar: baseAmount * 12,
      },
    };
  });

  return { results: templates };
};

export function computeSingleAutoStackStats(
  stack: AutoStack,
  purchases: AutoStackPurchasesResponse,
  marketData: Record<string, { priceInZar?: number; price?: number }>
): AutoStackStatsResponse {
  const completedPurchases = purchases.results.filter(
    (purchase) => purchase.status === "completed"
  );
  const scheduledPurchases = purchases.results.filter(
    (purchase) =>
      purchase.status === "scheduled" || purchase.status === "pending"
  );
  const scheduledFiatTotal = scheduledPurchases.reduce(
    (sum, purchase) => sum + purchase.amount_fiat,
    0
  );
  const totalStacked = completedPurchases.reduce(
    (sum, purchase) => sum + purchase.amount_fiat,
    0
  );
  const totalAccumulated = completedPurchases.reduce(
    (sum, purchase) => sum + purchase.amount_asset,
    0
  );
  const purchasedAssetBalance = completedPurchases.reduce(
    (sum, purchase) => sum + purchase.amount_asset,
    0
  );
  const assetBalance = toNumber(stack.asset_balance) || purchasedAssetBalance;
  const explicitTotalBalance = toNumber(stack.fiat_balance);
  const marketEntry = marketData[stack.assetSymbol?.toUpperCase() || ""];
  const marketPrice = marketEntry?.priceInZar || marketEntry?.price || 0;
  const totalBalance =
    explicitTotalBalance > 0
      ? explicitTotalBalance
      : assetBalance * marketPrice;
  const unitPrice =
    assetBalance > 0 ? totalBalance / assetBalance : marketPrice || 0;

  let runningAsset = 0;
  const performance = completedPurchases
    .sort(
      (left, right) =>
        new Date(left.scheduled_purchase_date).getTime() -
        new Date(right.scheduled_purchase_date).getTime()
    )
    .map((purchase) => {
      runningAsset += purchase.amount_asset;
      return {
        accumulatedAsset: runningAsset,
        fiatValue: runningAsset * unitPrice,
        timestamp: purchase.scheduled_purchase_date,
      };
    });

  if (performance.length === 0 && totalBalance > 0) {
    performance.push({
      accumulatedAsset: assetBalance,
      fiatValue: totalBalance,
      timestamp: new Date().toISOString(),
    });
  }

  return {
    assetBalance,
    totalStacked,
    totalAccumulated,
    avgBuyPrice: totalAccumulated > 0 ? totalStacked / totalAccumulated : 0,
    totalBalance,
    scheduledFiatTotal,
    performance,
    labels: performance.map((point) => point.timestamp),
  };
}

export const getSingleAutoStackStats = async (
  stackId: string,
  businessId?: string | null
): Promise<AutoStackStatsResponse> => {
  const [stack, purchases, marketResponse] = await Promise.all([
    getUserSingleAutoStack(stackId, businessId),
    getAllAutoStackPurchases(stackId, businessId),
    getMarketAssets(),
  ]);
  const marketData = (marketResponse.data || {}) as Record<
    string,
    { priceInZar?: number; price?: number }
  >;
  return computeSingleAutoStackStats(stack, purchases, marketData);
};

export const addToAutoStack = async (
  stackId: string,
  data: AddToAutoStackPayload,
  businessId?: string | null
): Promise<AutoStackPurchase> => {
  const response = await create<RawAutoStackPurchase>(
    autoStackUrlWithBusinessQuery(`/${stackId}/add-more`, businessId),
    data as unknown as Record<string, unknown>
  );
  return normalizePurchase(response);
};
